import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'

export type SightingRequestStatus = 'open' | 'answered' | 'declined' | 'expired'
export type SightingModality = 'visual' | 'thermal' | 'acoustic' | 'tactile'
export type ApplicabilityState = 'proposed' | 'active' | 'retired'

export interface ObserverReach {
  target_id: string
  latency_s: number
  modalities: SightingModality[]
  auto_interpret: boolean
  pointer?: string
  can_see?: string[]
  last_sighted_at?: string
}

export interface Observer {
  id: string
  kind: 'reviewer' | 'operators' | 'other'
  adapter?: string
  name: string
  config: Record<string, unknown>
  credential_configured: boolean
  enabled: boolean
  status: string
  reach?: ObserverReach[]
  created_at: string
  updated_at: string
}

export interface ConditionApplicability {
  id: string
  condition_id: string
  equipment_type: string
  how_to_check: string
  source: string
  llm_model?: string
  llm_confidence?: number
  llm_reasoning?: string
  state: ApplicabilityState
  asked: number
  answered: number
  discriminated: number
  created_at: string
  updated_at: string
}

export interface SightingRequestOutcomeCandidate {
  candidate_id: string
  outcome: 'support' | 'refute' | 'unknown'
}

export interface SightingRequestOutcome {
  candidates?: SightingRequestOutcomeCandidate[]
  discriminated?: boolean
  reason?: string
}

export interface SightingRequest {
  id: string
  occurrence_id?: string
  run_id?: string
  about_id: string
  condition_id?: string
  observable_id?: string
  applicability_id?: string
  question: string
  predictions: unknown
  status: SightingRequestStatus
  deadline_at?: string
  dispatched_to?: string
  outcome?: SightingRequestOutcome
  created_at: string
}

export interface SightingFinding {
  id?: string
  target_id: string
  present?: boolean
  value?: number
  unit?: string
  extent?: string
  confidence: number
  visible: 'yes' | 'partial' | 'no'
}

export interface SightingInterpretation {
  id?: string
  sighting_id?: string
  revision: number
  interpreted_at: string
  interpreted_by?: string
  interpreted_by_kind?: 'user' | 'model'
  vocabulary_version: number
  valid_until?: string
  findings: SightingFinding[]
}

export interface Sighting {
  id: string
  observer_id: string
  pointer?: string
  about_id: string
  observed_at: string
  recorded_at: string
  modality: SightingModality
  media_key?: string
  capture_conditions: string[]
  note: string
  request_id?: string
  occurrence_id?: string
  interpretation?: SightingInterpretation
}

export interface CreateSightingPayload {
  observer_id: string
  about_id: string
  observed_at: string
  modality: SightingModality
  capture_conditions: string[]
  note: string
  request_id?: string
  occurrence_id?: string
  interpretation: Omit<SightingInterpretation, 'id' | 'sighting_id' | 'revision'>
}

export interface ListSightingRequestsArgs {
  occurrenceId?: string
  aboutId?: string
  status?: SightingRequestStatus
  dispatchedTo?: string
  limit?: number
}

export interface ListSightingsArgs {
  aboutId?: string
  occurrenceId?: string
  conditionId?: string
  since?: string
  limit?: number
}

export interface ListApplicabilityArgs {
  conditionId?: string
  equipmentType?: string
  source?: string
  state?: ApplicabilityState
}

function queryString(entries: Array<[string, string | number | undefined]>): string {
  const params = new URLSearchParams()
  for (const [key, value] of entries) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function buildConditionAnswerPayload(args: {
  observerId: string
  request: Pick<SightingRequest, 'id' | 'about_id' | 'condition_id' | 'occurrence_id'>
  present: boolean
  observedAt: string
}): CreateSightingPayload {
  if (!args.request.condition_id) {
    throw new Error('A yes/no sighting answer requires a condition_id.')
  }
  return {
    observer_id: args.observerId,
    about_id: args.request.about_id,
    observed_at: args.observedAt,
    modality: 'visual',
    capture_conditions: [],
    note: '',
    request_id: args.request.id,
    ...(args.request.occurrence_id ? { occurrence_id: args.request.occurrence_id } : {}),
    interpretation: {
      interpreted_at: args.observedAt,
      vocabulary_version: 0,
      findings: [
        {
          target_id: args.request.condition_id,
          present: args.present,
          confidence: 1,
          visible: 'yes'
        }
      ]
    }
  }
}

export function buildConditionRevisionPayload(args: {
  conditionId: string
  present: boolean
  interpretedAt: string
}): Omit<SightingInterpretation, 'id' | 'sighting_id' | 'revision'> {
  return {
    interpreted_at: args.interpretedAt,
    vocabulary_version: 0,
    findings: [
      {
        target_id: args.conditionId,
        present: args.present,
        confidence: 1,
        visible: 'yes'
      }
    ]
  }
}

export const SIGHTINGS_TAG_TYPES = [
  'Observer',
  'ConditionApplicability',
  'SightingRequest',
  'Sighting'
] as const

export const sightingsApi = createApi({
  reducerPath: 'sightingsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: SIGHTINGS_TAG_TYPES,
  endpoints: (builder) => ({
    listObservers: builder.query<Observer[], { kind?: Observer['kind']; enabled?: boolean } | void>(
      {
        query: (args) =>
          `${PROXY_API_V1_URL}/ontology/observers${queryString([
            ['kind', args?.kind],
            ['enabled', args?.enabled === undefined ? undefined : String(args.enabled)]
          ])}`,
        transformResponse: (raw: { observers?: Observer[] }) => raw.observers ?? [],
        providesTags: (result) => [
          ...(result ?? []).map((observer) => ({
            type: 'Observer' as const,
            id: observer.id
          })),
          { type: 'Observer' as const, id: 'LIST' }
        ]
      }
    ),
    listSightingRequests: builder.query<SightingRequest[], ListSightingRequestsArgs | void>({
      query: (args) =>
        `${PROXY_API_V1_URL}/ontology/sighting-requests${queryString([
          ['occurrence_id', args?.occurrenceId],
          ['about_id', args?.aboutId],
          ['status', args?.status],
          ['dispatched_to', args?.dispatchedTo],
          ['limit', args?.limit]
        ])}`,
      transformResponse: (raw: { sighting_requests?: SightingRequest[] }) =>
        raw.sighting_requests ?? [],
      providesTags: (result) => [
        ...(result ?? []).map((request) => ({
          type: 'SightingRequest' as const,
          id: request.id
        })),
        { type: 'SightingRequest' as const, id: 'LIST' }
      ]
    }),
    declineSightingRequest: builder.mutation<
      SightingRequest,
      { requestId: string; reason?: string }
    >({
      query: ({ requestId, reason }) => ({
        url: `${PROXY_API_V1_URL}/ontology/sighting-requests/${encodeURIComponent(requestId)}:decline`,
        method: 'POST',
        body: { reason: reason ?? 'did_not_look' }
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'SightingRequest', id: requestId },
        { type: 'SightingRequest', id: 'LIST' }
      ]
    }),
    listSightings: builder.query<Sighting[], ListSightingsArgs | void>({
      query: (args) =>
        `${PROXY_API_V1_URL}/ontology/sightings${queryString([
          ['about_id', args?.aboutId],
          ['occurrence_id', args?.occurrenceId],
          ['condition_id', args?.conditionId],
          ['since', args?.since],
          ['limit', args?.limit]
        ])}`,
      transformResponse: (raw: { sightings?: Sighting[] }) => raw.sightings ?? [],
      providesTags: (result) => [
        ...(result ?? []).map((sighting) => ({
          type: 'Sighting' as const,
          id: sighting.id
        })),
        { type: 'Sighting' as const, id: 'LIST' }
      ]
    }),
    createSighting: builder.mutation<Sighting, CreateSightingPayload>({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/ontology/sightings`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result) => [
        { type: 'Sighting', id: result?.id ?? 'LIST' },
        { type: 'Sighting', id: 'LIST' },
        { type: 'SightingRequest', id: result?.request_id ?? 'LIST' },
        { type: 'SightingRequest', id: 'LIST' },
        { type: 'ConditionApplicability', id: 'LIST' }
      ]
    }),
    appendSightingInterpretation: builder.mutation<
      Sighting,
      {
        sightingId: string
        interpretation: Omit<SightingInterpretation, 'id' | 'sighting_id' | 'revision'>
      }
    >({
      query: ({ sightingId, interpretation }) => ({
        url: `${PROXY_API_V1_URL}/ontology/sightings/${encodeURIComponent(sightingId)}/interpretations`,
        method: 'POST',
        body: interpretation
      }),
      invalidatesTags: (result, _error, { sightingId }) => [
        { type: 'Sighting', id: sightingId },
        { type: 'Sighting', id: 'LIST' },
        { type: 'SightingRequest', id: result?.request_id ?? 'LIST' },
        { type: 'SightingRequest', id: 'LIST' },
        { type: 'ConditionApplicability', id: 'LIST' }
      ]
    }),
    listConditionApplicability: builder.query<
      ConditionApplicability[],
      ListApplicabilityArgs | void
    >({
      query: (args) =>
        `${PROXY_API_V1_URL}/ontology/conditions/applicability${queryString([
          ['condition_id', args?.conditionId],
          ['equipment_type', args?.equipmentType],
          ['source', args?.source],
          ['state', args?.state]
        ])}`,
      transformResponse: (raw: { applicability?: ConditionApplicability[] }) =>
        raw.applicability ?? [],
      providesTags: (result) => [
        ...(result ?? []).map((row) => ({
          type: 'ConditionApplicability' as const,
          id: row.id
        })),
        { type: 'ConditionApplicability' as const, id: 'LIST' }
      ]
    }),
    updateConditionApplicability: builder.mutation<
      ConditionApplicability,
      { id: string; how_to_check?: string; state?: ApplicabilityState }
    >({
      query: ({ id, ...body }) => ({
        url: `${PROXY_API_V1_URL}/ontology/conditions/applicability/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ConditionApplicability', id },
        { type: 'ConditionApplicability', id: 'LIST' }
      ]
    })
  })
})

export const {
  useListObserversQuery,
  useListSightingRequestsQuery,
  useDeclineSightingRequestMutation,
  useListSightingsQuery,
  useCreateSightingMutation,
  useAppendSightingInterpretationMutation,
  useListConditionApplicabilityQuery,
  useUpdateConditionApplicabilityMutation
} = sightingsApi
